<script>
    import Calendar from 'svelte-calendar';
    import { onMount } from 'svelte';

    import Upload from '../../components/upload.svelte';
    import { comoMoneda } from '../../utils';
    import {
        actualizacionesPrecios,
        aplicarActualizacionPrecio,
        aplicarActualizacionesAltaConfianza,
        cargarActualizacionesPrecios,
        descartarActualizacionesPrecios,
        rechazarActualizacionPrecio,
        traerActualizacionesPrecios,
    } from './_productos.svelte';

    let fecha = new Date();
    $: isoDate = fecha && fecha.toISOString().slice(0, 10);

    let archivo;
    let cargando = false;
    let procesando = false;
    let error = '';
    let resumen = null;

    $: pendientes = $actualizacionesPrecios.filter(
        ({ estado }) => estado === 'pendiente'
    );
    $: altaConfianza = pendientes.filter(
        ({ es_alta_confianza }) => es_alta_confianza
    );
    $: necesitaRevision = pendientes.filter(
        ({ es_alta_confianza }) => !es_alta_confianza
    );
    $: actualizacionesOrdenadas = [...$actualizacionesPrecios].sort(
        (a, b) => b.confianza - a.confianza
    );

    onMount(traerActualizacionesPrecios);

    async function subirArchivo() {
        if (!archivo) return;

        cargando = true;
        error = '';
        resumen = null;
        try {
            resumen = await cargarActualizacionesPrecios(archivo, isoDate);
        } catch (err) {
            error = 'No se pudo procesar el archivo.';
        } finally {
            cargando = false;
        }
    }

    async function aplicarAltaConfianza() {
        procesando = true;
        error = '';
        try {
            resumen = await aplicarActualizacionesAltaConfianza(isoDate);
        } catch (err) {
            error = 'No se pudieron aplicar las actualizaciones.';
        } finally {
            procesando = false;
        }
    }

    async function descartarTodas() {
        if (!confirm('Esto eliminara todas las actualizaciones pendientes.')) return;

        procesando = true;
        error = '';
        try {
            resumen = await descartarActualizacionesPrecios();
        } catch (err) {
            error = 'No se pudieron descartar las actualizaciones.';
        } finally {
            procesando = false;
        }
    }

    async function aplicarUna(id) {
        procesando = true;
        error = '';
        try {
            await aplicarActualizacionPrecio(id, isoDate);
        } catch (err) {
            error = 'No se pudo aplicar la actualizacion.';
        } finally {
            procesando = false;
        }
    }

    async function rechazarUna(id) {
        procesando = true;
        error = '';
        try {
            await rechazarActualizacionPrecio(id);
        } catch (err) {
            error = 'No se pudo rechazar la actualizacion.';
        } finally {
            procesando = false;
        }
    }

    function claseConfianza({ es_alta_confianza, motivo_revision }) {
        if (es_alta_confianza) return 'text-green-700';
        if (motivo_revision) return 'text-red-700';
        return 'text-yellow-700';
    }
</script>

<style>
    .btn-apply {
        @apply px-2 py-1 text-white bg-green-600 rounded;
    }

    .btn-apply:hover:not(:disabled) {
        @apply bg-green-700;
    }

    .btn-reject {
        @apply px-2 py-1 text-white bg-red-600 rounded;
    }

    .btn-reject:hover:not(:disabled) {
        @apply bg-red-700;
    }

    button:disabled {
        @apply cursor-not-allowed opacity-50;
    }

    .icon-button {
        min-width: 2rem;
    }
</style>

<div class="mx-6 mt-5">
    <div class="mb-4">
        <div class="text-xl font-bold">Actualizar Precios</div>
        <div class="text-sm text-gray-600">
            Sube archivos XLSX, revisa los matches y aplica solo lo aprobado.
        </div>
    </div>

    <div class="flex flex-wrap items-start gap-8 p-4 mb-5 bg-gray-100 border rounded">
        <div>
            <div class="mb-2 font-bold">Archivo XLSX</div>
            <Upload accept=".xlsx" bind:archivo>
                Seleccionar archivo
            </Upload>
        </div>

        <div>
            <div class="mb-2 font-bold">Fecha de aplicacion</div>
            <Calendar bind:selected="{fecha}" format="{'#{d}-#{m}-#{Y}'}" />
        </div>

        <div class="self-end mb-1">
            <button disabled="{!archivo || cargando}" on:click="{subirArchivo}">
                {cargando ? 'Procesando...' : 'Procesar archivo'}
            </button>
        </div>
    </div>

    {#if error}
        <div class="p-3 mb-4 text-red-700 bg-red-100 border border-red-300 rounded">
            {error}
        </div>
    {/if}

    {#if resumen}
        <div class="p-3 mb-4 text-blue-800 bg-blue-100 border border-blue-300 rounded">
            {#if resumen.total_procesadas !== undefined}
                Procesadas: {resumen.total_procesadas}. Creadas: {resumen.creadas}.
                Pendientes alta confianza: {resumen.pendientes_alta_confianza}.
            {:else if resumen.aplicadas !== undefined}
                Aplicadas: {resumen.aplicadas}.
            {:else if resumen.descartadas !== undefined}
                Descartadas: {resumen.descartadas}.
            {/if}
        </div>
    {/if}

    <div class="flex flex-wrap items-center gap-3 mb-4">
        <div class="mr-auto text-sm text-gray-700">
            Pendientes: <b>{pendientes.length}</b> · Alta confianza:
            <b>{altaConfianza.length}</b> · Revision:
            <b>{necesitaRevision.length}</b>
        </div>
        <button
            disabled="{!altaConfianza.length || procesando}"
            on:click="{aplicarAltaConfianza}"
            class="btn-apply"
        >
            Aplicar alta confianza
        </button>
        <button
            disabled="{!pendientes.length || procesando}"
            on:click="{descartarTodas}"
            class="btn-reject"
        >
            Descartar pendientes
        </button>
    </div>

    <div class="overflow-x-auto">
        <table class="w-full border sticky">
            <thead>
                <tr>
                    <th class="text-left">Archivo</th>
                    <th class="text-left">Sistema</th>
                    <th class="text-right">Precio actual</th>
                    <th class="text-right">Precio nuevo</th>
                    <th class="text-right">Confianza</th>
                    <th class="text-left">Estado</th>
                    <th class="text-center">Acciones</th>
                </tr>
            </thead>
            <tbody>
                {#each actualizacionesOrdenadas as actualizacion (actualizacion.id)}
                    <tr class:bg-green-100="{actualizacion.es_alta_confianza && actualizacion.estado === 'pendiente'}">
                        <td>
                            <div>{actualizacion.nombre_fuente}</div>
                            {#if actualizacion.motivo_revision}
                                <div class="text-xs text-red-700">
                                    {actualizacion.motivo_revision}
                                </div>
                            {/if}
                        </td>
                        <td>{actualizacion.producto_nombre || 'Sin match'}</td>
                        <td class="text-right">
                            {actualizacion.precio_actual
                                ? comoMoneda(actualizacion.precio_actual)
                                : '-'}
                        </td>
                        <td class="font-bold text-right">
                            {comoMoneda(actualizacion.precio_nuevo)}
                        </td>
                        <td class="text-right {claseConfianza(actualizacion)}">
                            {(actualizacion.confianza * 100).toFixed(1)}%
                        </td>
                        <td class="capitalize">{actualizacion.estado}</td>
                        <td class="text-center whitespace-no-wrap">
                            {#if actualizacion.estado === 'pendiente'}
                                <button
                                    disabled="{!actualizacion.producto || procesando}"
                                    on:click="{() => aplicarUna(actualizacion.id)}"
                                    class="icon-button btn-apply"
                                    title="Aplicar"
                                >
                                    <span class="material-icons text-base align-middle">check</span>
                                </button>
                                <button
                                    disabled="{procesando}"
                                    on:click="{() => rechazarUna(actualizacion.id)}"
                                    class="icon-button btn-reject"
                                    title="Rechazar"
                                >
                                    <span class="material-icons text-base align-middle">close</span>
                                </button>
                            {:else}
                                <span class="text-sm text-gray-500">Revisada</span>
                            {/if}
                        </td>
                    </tr>
                {:else}
                    <tr>
                        <td colspan="7" class="py-6 text-center text-gray-500">
                            No hay actualizaciones cargadas.
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
</div>
